/**
 * Redux state for organization-specific denial reasons from Firestore.
 *
 * @module redux/slice/denialReasonsSlice
 */

import { createSlice } from "@reduxjs/toolkit";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import {
  ReasonModel,
  ReasonsDocumentResponse,
} from "../../data-model/reasonModel";

/** Redux state for denial reasons loaded for the active organization. */
export interface DenialReasonsState {
  /** Denial reasons ordered for display. */
  data: ReasonModel[];
  /** True while {@link fetchDenialReasons} reads the Firestore document. */
  loading: boolean;
  /** Error recorded when the Firestore request fails. */
  error: string | null;
}

const initialState: DenialReasonsState = {
  data: [],
  loading: false,
  error: null,
};

const denialReasonsSlice = createSlice({
  name: "denialReasons",
  initialState,
  reducers: {
    setDenialReasons: (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchingDenialReasons: (state) => {
      state.loading = true;
      state.error = null;
      state.data = [];
    },
    setDenialReasonsError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.data = [];
    },
  },
});

export const {
  setDenialReasons,
  fetchingDenialReasons,
  setDenialReasonsError,
} = denialReasonsSlice.actions;

/**
 * Loads denial reasons configured for an organization.
 *
 * @param organization - Organization path segment, such as `oncology`.
 * @returns Redux thunk that stores the fetched reasons or error state.
 */
export const fetchDenialReasons =
  (organization: string) => async (dispatch, getState) => {
    const { denialReasons } = getState();
    if (denialReasons.loading) {
      return;
    }

    dispatch(fetchingDenialReasons());
    try {
      const response =
        await FirestoreService.getDocument<ReasonsDocumentResponse>(
          FirestoreDocumentReference.denialReasons(organization),
        );
      dispatch(setDenialReasons(response.reasons));
    } catch (error) {
      dispatch(setDenialReasonsError(error as string));
    }
  };

export default denialReasonsSlice.reducer;
